<main>
  <oaibox-stats-card
    title="Update Deployment"
    icon="fa-duotone fa-solid fa-building-user"
    gradient="primary"
    [outlined]="true"
  >
    <form
      class="form p-g"
      [formGroup]="updateDeploymentForm"
      (ngSubmit)="updateDeployment()"
    >
      <div class="form-row flex-wrap align-items-start">
        <div class="flex-fill d-flex g-g">
          @if (isRoot) {
            <oaibox-image-input
              [imageUrl]="tenantLogoUrl | authImage"
              imageDefaultUrl="assets/images/tenant.png"
              (imageFile)="addTenantLogo($event)"
              (deleteImage)="removeTenantLogo()"
            >
            </oaibox-image-input>
          }

          <div class="flex-fill">
            <label for="name" class="form-label">Name</label>
            <input
              id="name"
              type="text"
              class="form-control"
              [formControl]="updateDeploymentForm.controls.name"
              [readOnly]="!isRoot"
            />
          </div>
        </div>

        <div class="flex-fill">
          <label for="description" class="form-label">Description</label>
          <input
            id="description"
            type="text"
            class="form-control"
            [formControl]="updateDeploymentForm.controls.description"
            [readOnly]="!isRoot"
          />
        </div>

        <div class="flex-fill d-flex flex-column g-g">
          <div class="d-flex flex-column">
            <div
              class="d-inline-flex justify-content-between align-items-start g-g"
            >
              <label for="domain" class="form-label">Domain</label>

              @if (isRoot) {
                <div class="form-switch domain-auto-assignment">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    [formControl]="
                      updateDeploymentForm.controls.domainAutoAssignment
                    "
                    [readOnly]="!isRoot"
                  />
                  <label
                    >Let users with this email domain join automatically</label
                  >
                </div>
              }
            </div>

            @if (isRoot) {
              <button
                id="domain"
                type="button"
                class="form-control"
                (click)="openDeploymentDomainUpdateModal()"
                oaiboxBsPopover="Click to update this email domain"
                placement="left"
                trigger="hover"
                container="body"
                [disabled]="!isRoot"
              >
                {{ tenant?.clientDomain || "--" }}
                <i class="fa-solid fa-shield-halved"></i>
              </button>
            } @else {
              <input
                id="domain"
                type="text"
                class="form-control"
                [value]="tenant?.clientDomain"
                disabled
              />
            }
          </div>
        </div>
      </div>

      <div class="license-configuration">
        @let licenseConfiguration =
          updateDeploymentForm.controls.licenseConfiguration;

        <div class="license-configuration-toggle">
          <div class="form-switch">
            <input
              class="form-check-input"
              type="checkbox"
              role="switch"
              [formControl]="licenseConfiguration.controls.configurationIsShown"
            />
            <label>Show license configuration</label>
          </div>

          @if (isSales) {
            <div class="d-inline-flex align-items-baseline">
              <i class="fa-solid fa-triangle-exclamation warning"></i>
              <span id="warning-text"
                >These settings will only be updated when this option is
                enabled!</span
              >
            </div>
          }
        </div>

        @if (licenseConfiguration.value.configurationIsShown) {
          <div class="d-flex flex-wrap justify-content-between g-g">
            <div class="dropdown">
              <label for="licenseStatus" class="form-label">Status</label>
              <button
                id="licenseStatus"
                type="button"
                class="form-control dropdown-toggle"
                data-bs-toggle="dropdown"
                [disabled]="!isSales"
              >
                {{ licenseConfiguration.value.status ?? "Select status" }}
              </button>
              <ul class="dropdown-menu w-100">
                @for (licenseStatus of AVAILABLE_LICENSE_STATUS; track $index) {
                  <li>
                    <button
                      type="button"
                      class="dropdown-item"
                      [class.selected]="
                        licenseConfiguration.value.status === licenseStatus
                      "
                      (click)="onSelectLicenseStatus(licenseStatus)"
                    >
                      {{ licenseStatus }}
                    </button>
                  </li>
                }
              </ul>
            </div>

            <div class="d-flex g-g">
              <div>
                <label for="startDate" class="form-label">Start Date</label>
                <input
                  id="startDate"
                  class="form-control"
                  type="date"
                  [readOnly]="!isSales"
                  [formControl]="licenseConfiguration.controls.startDate"
                />
              </div>
              <div>
                <label for="endDate" class="form-label">End Date</label>
                <input
                  id="endDate"
                  class="form-control"
                  type="date"
                  [readOnly]="!isSales"
                  [formControl]="licenseConfiguration.controls.endDate"
                />
              </div>
            </div>
          </div>
        }
      </div>

      <div class="select-buttons">
        <div class="d-flex align-items-center g-g">
          <label class="select-buttons-label">Fronthaul Type</label>
          <div class="select-buttons-container">
            @for (
              type of updateDeploymentForm.controls.fronthaulTypes.controls;
              track $index
            ) {
              <button
                type="button"
                class="select-btn"
                [class.light]="!type.value.checked"
                (click)="toggleFormControl(type.controls.checked)"
                [disabled]="!isRoot"
              >
                {{ (type.value.type | fronthaulConfig)?.label }}
              </button>
            }
          </div>
        </div>

        <div class="d-flex align-items-center g-g">
          <label class="select-buttons-label">Frequency Range</label>
          <div class="select-buttons-container">
            @for (
              range of updateDeploymentForm.controls.frequencyRanges.controls;
              track $index
            ) {
              <button
                type="button"
                class="select-btn"
                [class.light]="!range.value.checked"
                (click)="toggleFormControl(range.controls.checked)"
                [disabled]="!isRoot"
              >
                {{ range.value.type }}
              </button>
            }
          </div>
        </div>

        <div class="d-flex align-items-center g-g">
          <label class="select-buttons-label">Duplex Mode</label>
          <div class="select-buttons-container">
            @for (
              mode of updateDeploymentForm.controls.duplexModes.controls;
              track $index
            ) {
              <button
                type="button"
                class="select-btn"
                [class.light]="!mode.value.checked"
                (click)="toggleFormControl(mode.controls.checked)"
                [disabled]="!isRoot"
              >
                {{ mode.value.type }}
              </button>
            }
          </div>
        </div>
      </div>

      <section class="licensing-configuration">
        <header>Licensing</header>
        <div class="licensing-list">
          <div class="licensing-list-group align-self-end">
            <div class="d-flex">
              @let education = updateDeploymentForm.controls.education;
              <button
                type="button"
                class="select-btn"
                [class.light]="!education.value"
                (click)="toggleFormControl(education)"
                [disabled]="!isRoot"
              >
                Education
              </button>

              @let kubernetes = updateDeploymentForm.controls.kubernetes;
              <button
                type="button"
                class="select-btn"
                [class.light]="!kubernetes.value"
                (click)="toggleFormControl(kubernetes)"
                [disabled]="!isRoot"
              >
                Kubernetes
              </button>

              @let advancedMode = updateDeploymentForm.controls.advancedMode;
              <button
                id="advanced-mode"
                type="button"
                class="select-btn"
                [class.light]="!advancedMode.value"
                (click)="toggleFormControl(advancedMode)"
                [disabled]="!isRoot"
              >
                Advanced Mode
              </button>

              @let cn5gRemote =
                updateDeploymentForm.controls.cn5gConfiguration.controls
                  .cn5gRemote;
              <button
                type="button"
                class="select-btn"
                [class.light]="!cn5gRemote.value"
                (click)="toggleFormControl(cn5gRemote)"
                [disabled]="!isRoot"
              >
                CN5G Remote
              </button>
            </div>
          </div>

          <div class="licensing-list-group">
            <header>Features</header>
            <div class="d-flex">
              @let flexRic = updateDeploymentForm.controls.flexRic;
              <button
                type="button"
                class="select-btn"
                [class.light]="!flexRic.value"
                (click)="toggleFormControl(flexRic)"
                [disabled]="!isRoot"
              >
                FlexRIC
              </button>

              @let ueManagement = updateDeploymentForm.controls.ueManagement;
              @if (ueManagement.controls.enabled.value) {
                <button
                  type="button"
                  class="select-btn"
                  (click)="toggleUeManagement()"
                  [disabled]="!isRoot"
                  [oaiboxBsPopover]="oaiUesPopover"
                  placement="bottom"
                  trigger="manual"
                  container="body"
                >
                  UE Management
                </button>
              } @else {
                <button
                  type="button"
                  class="select-btn light"
                  (click)="toggleUeManagement()"
                  [disabled]="!isRoot"
                >
                  UE Management
                </button>
              }

              @let networkSlicing =
                updateDeploymentForm.controls.networkSlicing;
              <button
                type="button"
                class="select-btn"
                [class.light]="!networkSlicing.value"
                (click)="toggleFormControl(networkSlicing)"
                [disabled]="!isRoot"
              >
                Network Slicing
              </button>
            </div>
          </div>

          <div class="licensing-list-group">
            <header>Premium Features</header>
            <div class="d-flex">
              @let mobility = updateDeploymentForm.controls.mobility;
              <button
                type="button"
                class="select-btn"
                [class.light]="!mobility.value"
                (click)="toggleFormControl(mobility)"
                [disabled]="!isRoot"
              >
                Mobility
              </button>

              @let qosium = updateDeploymentForm.controls.qosium;
              <button
                type="button"
                class="select-btn"
                [class.light]="!qosium.value"
                (click)="toggleFormControl(qosium)"
                [disabled]="!isRoot"
              >
                Qosium
              </button>

              @let ris = updateDeploymentForm.controls.ris;
              <button
                type="button"
                class="select-btn"
                [class.light]="!ris.value"
                (click)="toggleFormControl(ris)"
                [disabled]="!isRoot"
              >
                RIS
              </button>

              @let multiRan = updateDeploymentForm.controls.multiRan;
              <button
                type="button"
                class="select-btn"
                [class.light]="!multiRan.value"
                (click)="toggleFormControl(multiRan)"
                [disabled]="!isRoot"
              >
                Multi-RAN
              </button>

              @let redCap =
                updateDeploymentForm.controls.redCapConfiguration.controls
                  .redCap;
              <button
                type="button"
                class="select-btn"
                [class.light]="!redCap.value"
                (click)="toggleFormControl(redCap)"
                [disabled]="!isRoot"
              >
                RedCap
              </button>

              @let offlineEnabled =
                updateDeploymentForm.controls.offlineConfiguration.controls
                  .enabled.value;
              <button
                type="button"
                class="select-btn"
                [class.light]="!offlineEnabled"
                (click)="toggleOffline()"
                [disabled]="!isRoot"
              >
                Offline
              </button>
            </div>
          </div>

          <div class="licensing-list-group">
            <header>Use Cases Library</header>
            <div class="d-flex flex-wrap">
              @let digitalTwin = updateDeploymentForm.controls.digitalTwin;
              <button
                type="button"
                class="select-btn"
                [class.light]="!digitalTwin.value"
                (click)="toggleFormControl(digitalTwin)"
                [disabled]="!isRoot"
              >
                Digital Twin
              </button>

              @let isac = updateDeploymentForm.controls.isac;
              <button
                type="button"
                class="select-btn"
                [class.light]="!isac.value"
                (click)="toggleFormControl(isac)"
                [disabled]="!isRoot"
              >
                ISAC
              </button>

              @let radarDetection =
                updateDeploymentForm.controls.radarDetection;
              <button
                type="button"
                class="select-btn"
                [class.light]="!radarDetection.value"
                (click)="toggleFormControl(radarDetection)"
                [disabled]="!isRoot"
              >
                Radar Detection
              </button>

              @let visionAidedCommunications =
                updateDeploymentForm.controls.visionAidedCommunications;
              <button
                type="button"
                class="select-btn"
                [class.light]="!visionAidedCommunications.value"
                (click)="toggleFormControl(visionAidedCommunications)"
                [disabled]="!isRoot"
              >
                Vision Aided Communications
              </button>
            </div>
          </div>
        </div>
      </section>

      @let cn5gConfiguration = updateDeploymentForm.controls.cn5gConfiguration;
      @if (cn5gConfiguration.value.cn5gRemote) {
        <section class="cn5g-configuration">
          <header>CN5G Configuration</header>
          <div class="form cn5g-configuration-form">
            <div class="form-row flex-wrap">
              <div class="flex-fill">
                <label for="cn5g-ip" class="form-label">CN5G IP Address</label>
                <input
                  id="cn5g-ip"
                  type="text"
                  class="form-control"
                  [formControl]="cn5gConfiguration.controls.cn5gIp"
                  [readOnly]="!isRoot"
                />
              </div>

              <div class="flex-fill">
                <label for="nic-name" class="form-label">Interface Name</label>
                <input
                  id="cn5g-ip"
                  type="text"
                  class="form-control"
                  [formControl]="cn5gConfiguration.controls.cn5gInterfaceName"
                  [readOnly]="!isRoot"
                />
              </div>

              <div class="flex-fill">
                <label for="next-hop" class="form-label"
                  >Next Hop Address</label
                >
                <input
                  id="next-hop"
                  type="text"
                  class="form-control"
                  [formControl]="cn5gConfiguration.controls.cn5gNextHop"
                  [readOnly]="!isRoot"
                />
              </div>
            </div>

            @let cn5gFunctions =
              updateDeploymentForm.controls.cn5gConfiguration.controls;
            <section class="cn5g-functions">
              <span class="form-label">CN5G Functions</span>
              <div>
                <div>
                  <label for="amf-ip" class="form-label">AMF IP Address</label>
                  <input
                    id="amf-ip"
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gAmfIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div>
                  <label class="form-label">UPF IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gUpfIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div>
                  <label class="form-label">SMF IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gSmfIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div>
                  <label class="form-label">AUSF IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gAusfIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div>
                  <label class="form-label">UDM IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gUdmIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div>
                  <label class="form-label">UDR IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gUdrIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div>
                  <label class="form-label">NRF IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gNrfIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div>
                  <label class="form-label">NSSF IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gNssfIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div>
                  <label class="form-label">NEF IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gNefIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div>
                  <label class="form-label">NWDAF IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gNwdafIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div>
                  <label class="form-label">LMF IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gLmfIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div>
                  <label class="form-label">PCF IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gPcfIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div>
                  <label class="form-label">UDSF IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gFunctions.cn5gUdsfIp"
                    [readOnly]="!isRoot"
                  />
                </div>
              </div>
            </section>

            <section class="upf-components">
              <span class="form-label">UPF Components</span>
              <div class="form-row">
                <div class="flex-fill">
                  <label class="form-label">Open Speed Test IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="
                      cn5gConfiguration.controls.cn5gOpenSpeedTestIp
                    "
                    [readOnly]="!isRoot"
                  />
                </div>

                <div class="flex-fill">
                  <label class="form-label">OAI-EXT-DN IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gConfiguration.controls.cn5gOaiExtDnIp"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div class="flex-fill">
                  <label class="form-label">IMS IP Address</label>
                  <input
                    type="text"
                    class="form-control"
                    [formControl]="cn5gConfiguration.controls.cn5gImsIp"
                    [readOnly]="!isRoot"
                  />
                </div>
              </div>
            </section>
          </div>
        </section>
      }

      @let redCapConfiguration =
        updateDeploymentForm.controls.redCapConfiguration;
      @if (redCapConfiguration.value.redCap) {
        <section class="redcap-configuration">
          <header>RedCap Configuration</header>
          <div
            class="form redcap-configuration-form"
            [formGroup]="redCapConfiguration"
          >
            <div class="form-row flex-wrap">
              <div class="flex-fill">
                <label for="halfDuplex" class="form-label"
                  >Half Duplex RedCap Allowed</label
                >
                <input
                  id="halfDuplex"
                  type="number"
                  class="form-control"
                  [formControl]="
                    redCapConfiguration.controls.halfDuplexRedCapAllowedR17
                  "
                  [readOnly]="!isRoot"
                />
              </div>

              <div class="flex-fill">
                <label for="cellBarredRedCap1" class="form-label"
                  >Cell Barred RedCap1 Rx</label
                >
                <input
                  id="cellBarredRedCap1"
                  type="number"
                  class="form-control"
                  [formControl]="
                    redCapConfiguration.controls.cellBarredRedCap1RxR17
                  "
                  [readOnly]="!isRoot"
                />
              </div>

              <div class="flex-fill">
                <label for="cellBarredRedCap2" class="form-label"
                  >Cell Barred RedCap2 Rx</label
                >
                <input
                  id="cellBarredRedCap2"
                  type="number"
                  class="form-control"
                  [formControl]="
                    redCapConfiguration.controls.cellBarredRedCap2RxR17
                  "
                  [readOnly]="!isRoot"
                />
              </div>
              <div class="flex-fill">
                <label for="intraFreqReselection" class="form-label"
                  >Intra Freq. Reselection RedCap</label
                >
                <input
                  id="intraFreqReselection"
                  type="number"
                  class="form-control"
                  [formControl]="
                    redCapConfiguration.controls.intraFreqReselectionRedCapR17
                  "
                  [readOnly]="!isRoot"
                />
              </div>
            </div>

            <section class="timers-configuration">
              <span class="form-label">Timers Configuration</span>
              <div>
                <div class="flex-fill">
                  <label for="t300" class="form-label">T300</label>
                  <input
                    id="t300"
                    type="number"
                    class="form-control"
                    [formControl]="redCapConfiguration.controls.t300"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div class="flex-fill">
                  <label for="t301" class="form-label">T301</label>
                  <input
                    id="t301"
                    type="number"
                    class="form-control"
                    [formControl]="redCapConfiguration.controls.t301"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div class="flex-fill">
                  <label for="t310" class="form-label">T310</label>
                  <input
                    id="t310"
                    type="number"
                    class="form-control"
                    [formControl]="redCapConfiguration.controls.t310"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div class="flex-fill">
                  <label for="t311" class="form-label">T311</label>
                  <input
                    id="t311"
                    type="number"
                    class="form-control"
                    [formControl]="redCapConfiguration.controls.t311"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div class="flex-fill">
                  <label for="n310" class="form-label">N310</label>
                  <input
                    id="n310"
                    type="number"
                    class="form-control"
                    [formControl]="redCapConfiguration.controls.n310"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div class="flex-fill">
                  <label for="n311" class="form-label">N311</label>
                  <input
                    id="n311"
                    type="number"
                    class="form-control"
                    [formControl]="redCapConfiguration.controls.n311"
                    [readOnly]="!isRoot"
                  />
                </div>

                <div class="flex-fill">
                  <label for="t319" class="form-label">T319</label>
                  <input
                    id="t319"
                    type="number"
                    class="form-control"
                    [formControl]="redCapConfiguration.controls.t319"
                    [readOnly]="!isRoot"
                  />
                </div>
              </div>
            </section>
          </div>
        </section>
      }

      <footer>
        <button
          type="submit"
          class="btn btn-success"
          [disabled]="
            ongoingRequest ||
            !updateDeploymentForm.dirty ||
            updateDeploymentForm.invalid
          "
        >
          @if (ongoingRequest) {
            <i class="fa-solid fa-spinner rotating"></i>
          } @else {
            Save
          }
        </button>
      </footer>
    </form>
  </oaibox-stats-card>
</main>

<ng-template #oaiUesPopover>
  @let oaiUeMachineIds =
    updateDeploymentForm.controls.ueManagement.controls.oaiUeMachineIds;
  <div class="oai-ues-popover">
    <span class="oai-ues-popover-title">OAI UE Machine IDs</span>
    @for (machineId of oaiUeMachineIds.controls; track $index) {
      <div class="oai-ues-popover-row">
        <div class="errors-wrapper">
          <input
            type="text"
            class="form-control"
            placeholder="Machine ID"
            [formControl]="machineId"
            [class.invalid-input]="machineId.invalid && machineId.dirty"
          />
          @if (machineId.invalid && machineId.dirty) {
            <div class="input-error">Invalid UUID format</div>
          }
        </div>
        <i
          class="fa-solid fa-minus oai-ues-remove"
          (click)="removeOaiUeMachineId($index)"
        ></i>
      </div>
    }
    <i class="fa-solid fa-plus oai-ues-add" (click)="addOaiUeMachineId()"></i>
  </div>
</ng-template>
